/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useNotification from "../../../hooks/useNotification";
import Form from "../../../components/forms/Form";
import {
  cleanPayload,
  normalizeFormErrors,
  transformFormDataForApi,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import ItemListSection from "../../../components/forms/ItemListSection";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box } from "@mui/material";
import { useEventPageLogic } from "./hook/useEventPageLogic";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import { v4 as uuidv4 } from "uuid";
import EventTypeEnum, { getEventName } from "../../../enums/event-type-enum";

const EditEventContractPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { contractId, eventId } = useParams();

  const { get: baseServiceGet, put: baseServicePut } = useBaseService(navigate);

  const queryClient = useQueryClient();

  const {
    form,
    event,
    aditionRegister,
    aditionTerm,
    aditionQuantity,
    aditionPrice,
    aditionSupression,
    aditionModifyItem,
    aditionContractAmendment,
  } = useEventPageLogic({
    contractId,
    isEditPage: true,
  });

  const { data: eventDataForEdit } = useQueryWithLoader({
    queryKey: ["eventDataForEdit", contractId, eventId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.eventAdditivesForEdit(contractId, eventId),
      );
      // * Retornar o ultimo evento
      return response.result;
    },
    enabled: !!contractId && !!eventId,
  });

  const eventTypeId = eventDataForEdit?.event_type_id;

  useEffect(() => {
    if (!eventDataForEdit || !eventTypeId) return;

    event.setFormData({
      event: {
        event_id: eventId,
        contract_event_type_id: [
          {
            value: eventDataForEdit.event_type_id,
            label: getEventName(eventDataForEdit.event_type_id),
          },
        ],
      },
    });

    const {
      suppression_changes: suppressionChanges,
      quantity_changes: quantityChanges,
      price_changes: priceChanges,
      term_change: termChange,
      modify_item_changes: modifyItemChanges,
      amendment_changes: amendmentChanges,
      register_changes: registerChanges,
    } = eventDataForEdit;

    // ADITIVO DE SUPRESSÃO - mostrar quantidade SUPRIMIDA (não a final)
    if (
      eventTypeId === EventTypeEnum["ADITIVO DE SUPRESSÃO"] &&
      suppressionChanges?.length > 0
    ) {
      const mapped = suppressionChanges.map((change) => {
        return {
          id: uuidv4().slice(0, 8),
          root_item_id: change.root_item_id,
          contract_item_id: {
            value: change.contract_item_id,
            label: `${change.alias} -   ${change.description} - Qtd. Atual: ${change.original_quantity} - Disponível para Supressão: ${change.original_available}`,
          }, // ID da versão ANTERIOR
          quantity: change.quantity_suppressed, // <-- EDITÁVEL: quantidade suprimida
          original_quantity: change.original_quantity, // Para referência visual
          original_available: change.original_available,
          alias: change.alias,
          description: change.description,
        };
      });

      aditionSupression.setAditionSupression(mapped);
      // Não seta formData pois o ItemListSection usa estado local
    } else if (eventTypeId === EventTypeEnum["ADITIVO DE SUPRESSÃO"]) {
      // Array vazio - usuário pode adicionar novos itens para suprimir se quiser corrigir algo
      aditionSupression.setAditionSupression([]);
    }

    // ADITIVO DE QUANTIDADE - mostrar aumento aplicado
    if (
      eventTypeId === EventTypeEnum["ADITIVO DE QUANTIDADE"] &&
      quantityChanges?.length > 0
    ) {
      const mapped = quantityChanges.map((change) => ({
        id: uuidv4().slice(0, 8),
        root_item_id: change.root_item_id,
        contract_item_id: change.contract_item_id || null,
        quantity: change.quantity_added, // <-- EDITÁVEL: quantidade adicionada
        new: change.new || false,
        alias: change.alias || "",
        description: change.description || "",
        price: change.price || 0,
        original_quantity: change.original_quantity, // Para referência
      }));

      aditionQuantity.setAditionQuantity(mapped);
    } else if (eventTypeId === EventTypeEnum["ADITIVO DE QUANTIDADE"]) {
      aditionQuantity.setAditionQuantity([]);
    }

    // ADITIVO DE REAJUSTE - mostrar novo preço
    if (
      eventTypeId === EventTypeEnum["ADITIVO DE REAJUSTE"] &&
      priceChanges?.length > 0
    ) {
      const mapped = priceChanges.map((change) => ({
        id: uuidv4().slice(0, 8),
        root_item_id: change.root_item_id,
        root_job_id: change.root_job_id,
        contract_item_id:
          change.type === "item"
            ? {
                value: change.contract_item_id,
                label: `${change.alias} -   ${change.description}`,
              }
            : null,
        contract_job_id:
          change.type === "job"
            ? {
                value: change.contract_job_id,
                label: `${change.alias} -   ${change.description}`,
              }
            : null,
        item_or_job: {
          value: change.type === "item",
          label: change.type === "item" ? "Item do Contrato" : "Serviço",
        },
        price: change.new_price, // <-- EDITÁVEL: novo preço
        original_price: change.original_price,
        alias: change.alias,
      }));

      aditionPrice.setAditionPrice(mapped);
    } else if (eventTypeId === EventTypeEnum["ADITIVO DE REAJUSTE"]) {
      aditionPrice.setAditionPrice([]);
    }

    // ADITIVO DE PRAZO
    if (eventTypeId === EventTypeEnum["ADITIVO DE PRAZO"]) {
      const currentInfo = Array.isArray(eventDataForEdit.event_additive?.infos)
        ? eventDataForEdit.event_additive.infos[0]
        : eventDataForEdit.event_additive?.infos;
      const currentEndDate =
        termChange?.new_end_date ?? currentInfo?.end_date ?? "";

      aditionTerm.setFormData({
        aditionTerm: {
          end_date: currentEndDate, // Data atual do aditivo, com fallback ao relacionamento carregado.
        },
      });
    }

    // ADITIVO DE MODIFICAÇÃO DE ITEM
    if (
      eventTypeId === EventTypeEnum["ADITIVO MODIFICAÇÃO DE ITEM"] &&
      modifyItemChanges?.length > 0
    ) {
      const mapped = modifyItemChanges.map((change) => ({
        id: uuidv4().slice(0, 8),
        root_item_id: change.root_item_id,
        contract_item_id: {
          value: change.contract_item_id,
          label: `${change.original_alias} -   ${change.original_description}`,
        },
        alias: change.new_alias, // <-- EDITÁVEL
        description: change.new_description, // <-- EDITÁVEL
        quantity: change.new_quantity, // <-- EDITÁVEL
        price: change.new_price, // <-- EDITÁVEL
        original_alias: change.original_alias,
        original_description: change.original_description,
        original_quantity: change.original_quantity,
        original_price: change.original_price,
      }));

      aditionModifyItem.setAditionModifyItem(mapped);
    } else if (eventTypeId === EventTypeEnum["ADITIVO MODIFICAÇÃO DE ITEM"]) {
      aditionModifyItem.setAditionModifyItem([]);
    }

    // APOSTILAMENTO
    if (eventTypeId === EventTypeEnum.APOSTILAMENTO && amendmentChanges) {
      // Datas
      if (Object.keys(amendmentChanges.info).length > 0) {
        const infoData = {};
        Object.entries(amendmentChanges.info).forEach(([field, values]) => {
          infoData[field] = values.new; // <-- EDITÁVEL
        });

        aditionContractAmendment.setFormData((prev) => ({
          ...prev,
          aditionContractAmendment: infoData,
        }));
      }

      // Itens
      if (amendmentChanges.items?.length > 0) {
        const mappedItems = amendmentChanges.items.map((change) => ({
          id: uuidv4().slice(0, 8),
          root_item_id: change.root_item_id,
          contract_item_id: {
            value: change.contract_item_id,
            label: `${change.new_alias} -   ${change.new_description}`,
          },
          alias: change.new_alias, // <-- EDITÁVEL
          description: change.new_description, // <-- EDITÁVEL
          original_alias: change.original_alias,
          original_description: change.original_description,
          original_quantity: change.original_quantity,
          original_price: change.original_price,
        }));

        aditionContractAmendment.items.setAditionContractAmendmentItems(
          mappedItems,
        );
      }

      // Serviços
      if (amendmentChanges.jobs?.length > 0) {
        const mappedJobs = amendmentChanges.jobs.map((change) => ({
          id: uuidv4().slice(0, 8),
          root_job_id: change.root_job_id,
          contract_job_id: {
            value: change.contract_job_id,
            label: `${change.alias} -   ${change.description}`,
          },
          alias: change.new_alias, // <-- EDITÁVEL
          description: change.new_description, // <-- EDITÁVEL
          original_alias: change.original_alias,
          original_description: change.original_description,
          original_quantity: change.original_quantity,
          original_price: change.original_price,
        }));

        aditionContractAmendment.jobs.setAditionContractAmendmentJobs(
          mappedJobs,
        );
      }
    } else if (eventTypeId === EventTypeEnum.APOSTILAMENTO) {
      aditionContractAmendment.items.setAditionContractAmendmentItems([]);
      aditionContractAmendment.jobs.setAditionContractAmendmentJobs([]);
    }

    // REGISTRO
    if (eventTypeId === EventTypeEnum.CADASTRO && registerChanges) {
      if (Object.keys(registerChanges.contract).length > 0) {
        const contractData = {};
        Object.entries(registerChanges.contract).forEach(([field, values]) => {
          // Para selects, precisa do formato { value, label }
          if (field === "contract_type_id") {
            contractData[field] = {
              value: values.current_contract_type_id,
              label: values.current_contract_type_name,
            };
          } else if (field === "contract_status_id") {
            contractData[field] = {
              value: values.current_contract_status_id,
              label: values.current_contract_status_name,
            };
          } else if (field === "customer_id") {
            contractData[field] = {
              value: values.current_customer_id,
              label: values.current_customer_name,
            };
          } else if (field === "organization_id") {
            contractData[field] = {
              value: values.current_organization_id,
              label: values.current_organization_name,
            };
          } else {
            contractData[field] = values.new;
          }
        });

        aditionRegister.setFormData((prev) => ({
          ...prev,
          contract: contractData,
        }));
      }

      // Datas
      if (Object.keys(registerChanges.info).length > 0) {
        const infoData = {};
        Object.entries(registerChanges.info).forEach(([field, values]) => {
          infoData[field] = values.new; // <-- EDITÁVEL
        });

        aditionRegister.setFormData((prev) => ({
          ...prev,
          info: infoData,
        }));
      }

      // Itens
      if (registerChanges.items?.length > 0) {
        const mappedItems = registerChanges.items.map((change) => ({
          id: uuidv4().slice(0, 8),
          root_item_id: change.root_item_id,
          contract_item_id: {
            value: change.contract_item_id,
            label: `${change.new_alias} -   ${change.new_description}`,
          },
          alias: change.new_alias, // <-- EDITÁVEL
          description: change.new_description, // <-- EDITÁVEL
          price: change.new_price, // <-- EDITÁVEL
          quantity: change.new_quantity, // <-- EDITÁVEL
          original_alias: change.original_alias,
          original_description: change.original_description,
          original_price: change.original_price,
          original_quantity: change.original_quantity,
        }));

        aditionRegister.items.setAditionRegisterItems(mappedItems);
      }

      // Serviços
      if (registerChanges.jobs?.length > 0) {
        const mappedJobs = registerChanges.jobs.map((change) => ({
          id: uuidv4().slice(0, 8),
          root_job_id: change.root_job_id,
          contract_job_id: {
            value: change.contract_job_id,
            label: `${change.alias} -   ${change.description}`,
          },
          alias: change.new_alias, // <-- EDITÁVEL
          description: change.new_description, // <-- EDITÁVEL
          price: change.new_price, // <-- EDITÁVEL
          quantity: change.new_quantity, // <-- EDITÁVEL
          original_alias: change.original_alias,
          original_description: change.original_description,
          original_price: change.original_price,
          original_quantity: change.original_quantity,
        }));

        aditionRegister.jobs.setAditionRegisterJobs(mappedJobs);
      }
    } else if (eventTypeId === EventTypeEnum.APOSTILAMENTO) {
      aditionRegister.items.setAditionRegisterItems([]);
      aditionRegister.jobs.setAditionRegisterJobs([]);
    }
  }, [eventDataForEdit, eventTypeId]);

  const updateEventMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await baseServicePut(
        entities.contracts.contractEventUpdate(contractId, eventId),
        payload,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        event.resetForm();
        navigate(`/aluguel/contratos`);
        queryClient.invalidateQueries(["contractDetails", contractId]);
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      // const errorMessage = error.response?.data?.error || 'Erro ao criar o evento do contrato.';
      // showNotification('error', errorMessage);
      if (error.response?.data?.errors) {
        form.setErrors(error.response.data.errors);
      }
    },
  });

  const handleSubmit = async () => {
    try {
      const selectedValues = event.selects.eventTypesSelect.selectedValue.map(
        (item) => item.value,
      );

      for (const value of selectedValues) {
        switch (value) {
          case 3:
            if (aditionQuantity.aditionQuantity.length < 1) {
              showNotification(
                "warning",
                "Preencha pelo menos um item de aditivo de quantidade",
              );
              return;
            }
            break;

          case 4:
            if (aditionPrice.aditionPrice.length < 1) {
              showNotification(
                "warning",
                "Preencha pelo menos um item de aditivo de reajuste",
              );
              return;
            }
            break;

          case 5:
            if (aditionSupression.aditionSupression.length < 1) {
              showNotification(
                "warning",
                "Preencha pelo menos um item de aditivo de supressão",
              );
              return;
            }
            break;
          case 6:
            if (aditionModifyItem.aditionModifyItem.length < 1) {
              showNotification(
                "warning",
                "Preencha pelo menos um item de aditivo de modificação de item",
              );
              return;
            }
            break;

          default:
            break;
        }
      }

      const payload = {
        ...event.formData,
        ...aditionTerm.formData,
        aditionRegister: {
          ...aditionRegister.formData,
          items: aditionRegister.items.aditionRegisterItems,
          jobs: aditionRegister.jobs.aditionRegisterJobs,
        },
        aditionQuantity: aditionQuantity.aditionQuantity,
        aditionPrice: aditionPrice.aditionPrice,
        aditionSupression: aditionSupression.aditionSupression,
        aditionModifyItem: aditionModifyItem.aditionModifyItem,
        aditionContractAmendment: {
          ...aditionContractAmendment.formData.aditionContractAmendment,
          items: aditionContractAmendment.items.aditionContractAmendmentItems,
          jobs: aditionContractAmendment.jobs.aditionContractAmendmentJobs,
        },
      };

      const transformedFormData = transformFormDataForApi(payload);
      const cleanedPayload = cleanPayload(transformedFormData, {
        skipNumberConversionFields: ["aditionRegister.contract.number"],
      });

      return await updateEventMutation.mutateAsync(cleanedPayload);
    } catch (error) {
      console.log("Erro ao atualizar evento do contrato:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = useCallback(() => {
    navigate(`/aluguel/contratos`);
  }, [navigate]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar Evento do Contrato"
        showBackButton={false}
        backUrl={`/aluguel/contratos`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Atualizar"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
          isLoading={updateEventMutation.isPending}>
          {() => (
            <>
              <div key={event.section}>
                <SectionHeader
                  section={event.section}
                  description={event.description}
                />
                <SimpleBody
                  fields={event.fieldsState}
                  formErrors={normalizeFormErrors(form.errors)}
                  formData={event.formData}
                  handleFieldChange={event.handleChange}
                  getOptions={event.getOptions}
                  getSelectedValue={event.getSelectedValue}
                  getSelectMeta={event.getSelectMeta}
                />
              </div>
              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum.CADASTRO) && (
                <div key={aditionRegister.section}>
                  {/* Campos GLOBAIS (datas) */}
                  <SectionHeader
                    section={aditionRegister.section}
                    description={aditionRegister.description}
                  />
                  <SimpleBody
                    fields={aditionRegister.fields}
                    formData={aditionRegister.formData}
                    handleFieldChange={aditionRegister.handleChange}
                    formErrors={normalizeFormErrors(form.errors)}
                    getOptions={aditionRegister.getOptions}
                    getSelectedValue={aditionRegister.getSelectedValue}
                    getSelectMeta={aditionRegister.getSelectMeta}
                  />

                  {/* Lista de ITEMS */}
                  <Box sx={{ mt: "1rem" }}>
                    <ItemListSection
                      section={aditionRegister.items.section}
                      description={aditionRegister.items.description}
                      fields={aditionRegister.items.fields}
                      initialData={aditionRegister.items.aditionRegisterItems}
                      onItemsChange={
                        aditionRegister.items.setAditionRegisterItems
                      }
                      renderFormBody={
                        aditionRegister.items.renderAditionRegisterItemsFormBody
                      }
                      getItemDisplayValue={
                        aditionRegister.items
                          .getAditionRegisterItemsDisplayValue
                      }
                      itemFormData={aditionRegister.items.formData}
                      onItemChange={aditionRegister.items.handleChange}
                      itemFormErrors={form.errors}
                      onFormSubmit={
                        aditionRegister.items.handleSaveAditionRegisterItems
                      }
                      initializeItemForm={
                        aditionRegister.items.initializeAditionRegisterItemsForm
                      }
                      getOptions={aditionRegister.items.getOptions}
                      getSelectedValue={aditionRegister.items.getSelectedValue}
                      getSelectMeta={aditionRegister.items.getSelectMeta}
                    />
                  </Box>

                  {/* Lista de JOBS */}
                  <Box sx={{ mt: "1rem" }}>
                    <ItemListSection
                      section={aditionRegister.jobs.section}
                      description={aditionRegister.jobs.description}
                      fields={aditionRegister.jobs.fields}
                      initialData={aditionRegister.jobs.aditionRegisterJobs}
                      onItemsChange={
                        aditionRegister.jobs.setAditionRegisterJobs
                      }
                      renderFormBody={
                        aditionRegister.jobs.renderAditionRegisterJobsFormBody
                      }
                      getItemDisplayValue={
                        aditionRegister.jobs.getAditionRegisterJobsDisplayValue
                      }
                      itemFormData={aditionRegister.jobs.formData}
                      onItemChange={aditionRegister.jobs.handleChange}
                      itemFormErrors={form.errors}
                      onFormSubmit={
                        aditionRegister.jobs.handleSaveAditionRegisterJobs
                      }
                      initializeItemForm={
                        aditionRegister.jobs.initializeAditionRegisterJobsForm
                      }
                      getOptions={aditionRegister.jobs.getOptions}
                      getSelectedValue={aditionRegister.jobs.getSelectedValue}
                      getSelectMeta={aditionRegister.jobs.getSelectMeta}
                    />
                  </Box>
                </div>
              )}
              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO DE PRAZO"]) && (
                <div key={aditionTerm.section}>
                  <SectionHeader
                    section={aditionTerm.section}
                    description={aditionTerm.description}
                  />
                  <SimpleBody
                    fields={aditionTerm.fields}
                    formErrors={normalizeFormErrors(form.errors)}
                    formData={aditionTerm.formData}
                    handleFieldChange={aditionTerm.handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              )}
              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO DE QUANTIDADE"]) && (
                <Box
                  key={aditionQuantity.section}
                  sx={{ mt: "1rem", display: "flex", flexDirection: "column" }}>
                  <ItemListSection
                    section={aditionQuantity.section}
                    description={aditionQuantity.description}
                    fields={aditionQuantity.fields}
                    fieldsState={aditionQuantity.fieldsState}
                    initialData={aditionQuantity.aditionQuantity}
                    onItemsChange={aditionQuantity.handleAditionsQuantityChange}
                    renderFormBody={
                      aditionQuantity.renderAditionQuantityFormBody
                    }
                    getItemDisplayValue={
                      aditionQuantity.getAditionQuantityDisplayValue
                    }
                    itemFormData={aditionQuantity.formData}
                    onItemChange={aditionQuantity.handleChange}
                    itemFormErrors={form.errors}
                    onFormSubmit={aditionQuantity.handleSaveAditionQuantity}
                    initializeItemForm={
                      aditionQuantity.initializeAditionQuantityForm
                    }
                    isDisabledButton={false}
                    getOptions={aditionQuantity.getOptions}
                    getSelectedValue={aditionQuantity.getSelectedValue}
                    getSelectMeta={aditionQuantity.getSelectMeta}
                    notShowHeader={["aditionQuantity.new"]}
                  />
                </Box>
              )}
              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO DE REAJUSTE"]) && (
                <Box
                  key={aditionPrice.section}
                  sx={{ mt: "1rem", display: "flex", flexDirection: "column" }}>
                  <ItemListSection
                    section={aditionPrice.section}
                    description={aditionPrice.description}
                    fields={aditionPrice.fields}
                    fieldsState={aditionPrice.fieldsState}
                    initialData={aditionPrice.aditionPrice}
                    onItemsChange={aditionPrice.handleAditionsPriceChange}
                    renderFormBody={aditionPrice.renderAditionPriceFormBody}
                    getItemDisplayValue={
                      aditionPrice.getAditionPriceDisplayValue
                    }
                    itemFormData={aditionPrice.formData}
                    onItemChange={aditionPrice.handleChange}
                    itemFormErrors={form.errors}
                    onFormSubmit={aditionPrice.handleSaveAditionPrice}
                    initializeItemForm={aditionPrice.initializeAditionPriceForm}
                    isDisabledButton={false}
                    getOptions={aditionPrice.getOptions}
                    getSelectedValue={aditionPrice.getSelectedValue}
                    getSelectMeta={aditionPrice.getSelectMeta}
                    notShowHeader={["aditionPrice.item_or_job"]}
                  />
                </Box>
              )}
              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO DE SUPRESSÃO"]) && (
                <Box
                  key={aditionSupression.section}
                  sx={{ mt: "1rem", display: "flex", flexDirection: "column" }}>
                  <ItemListSection
                    section={aditionSupression.section}
                    description={aditionSupression.description}
                    fields={aditionSupression.fields}
                    fieldsState={aditionSupression.fieldsState}
                    initialData={aditionSupression.aditionSupression}
                    onItemsChange={
                      aditionSupression.handleAditionsSupressionChange
                    }
                    renderFormBody={
                      aditionSupression.renderAditionSupressionFormBody
                    }
                    getItemDisplayValue={
                      aditionSupression.getAditionSupressionDisplayValue
                    }
                    itemFormData={aditionSupression.formData}
                    onItemChange={aditionSupression.handleChange}
                    itemFormErrors={form.errors}
                    onFormSubmit={aditionSupression.handleSaveAditionSupression}
                    initializeItemForm={
                      aditionSupression.initializeAditionSupressionForm
                    }
                    isDisabledButton={false}
                    getOptions={aditionSupression.getOptions}
                    getSelectedValue={aditionSupression.getSelectedValue}
                    getSelectMeta={aditionSupression.getSelectMeta}
                  />
                </Box>
              )}
              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO MODIFICAÇÃO DE ITEM"]) && (
                <Box
                  key={aditionModifyItem.section}
                  sx={{ mt: "1rem", display: "flex", flexDirection: "column" }}>
                  <ItemListSection
                    section={aditionModifyItem.section}
                    description={aditionModifyItem.description}
                    fields={aditionModifyItem.fields}
                    initialData={aditionModifyItem.aditionModifyItem}
                    onItemsChange={
                      aditionModifyItem.handleAditionsModifyItemChange
                    }
                    renderFormBody={
                      aditionModifyItem.renderAditionModifyItemFormBody
                    }
                    getItemDisplayValue={
                      aditionModifyItem.getAditionModifyItemDisplayValue
                    }
                    itemFormData={aditionModifyItem.formData}
                    onItemChange={aditionModifyItem.handleChange}
                    itemFormErrors={form.errors}
                    onFormSubmit={aditionModifyItem.handleSaveAditionModifyItem}
                    initializeItemForm={
                      aditionModifyItem.initializeAditionModifyItemForm
                    }
                    isDisabledButton={false}
                    getOptions={aditionModifyItem.getOptions}
                    getSelectedValue={aditionModifyItem.getSelectedValue}
                    getSelectMeta={aditionModifyItem.getSelectMeta}
                  />
                </Box>
              )}
              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum.APOSTILAMENTO) && (
                <div key={aditionContractAmendment.section}>
                  {/* Campos GLOBAIS (datas) */}
                  <SectionHeader
                    section={aditionContractAmendment.section}
                    description={aditionContractAmendment.description}
                  />
                  <SimpleBody
                    fields={aditionContractAmendment.fields}
                    formData={aditionContractAmendment.formData}
                    handleFieldChange={aditionContractAmendment.handleChange}
                    formErrors={normalizeFormErrors(form.errors)}
                    getOptions={aditionContractAmendment.getOptions}
                    getSelectedValue={aditionContractAmendment.getSelectedValue}
                    getSelectMeta={aditionContractAmendment.getSelectMeta}
                  />

                  {/* Lista de ITEMS */}
                  <Box sx={{ mt: "1rem" }}>
                    <ItemListSection
                      section={aditionContractAmendment.items.section}
                      description={aditionContractAmendment.items.description}
                      fields={aditionContractAmendment.items.fields}
                      initialData={
                        aditionContractAmendment.items
                          .aditionContractAmendmentItems
                      }
                      onItemsChange={
                        aditionContractAmendment.items
                          .setAditionContractAmendmentItems
                      }
                      renderFormBody={
                        aditionContractAmendment.items
                          .renderAditionContractAmendmentItemsFormBody
                      }
                      getItemDisplayValue={
                        aditionContractAmendment.items
                          .getAditionContractAmendmentItemsDisplayValue
                      }
                      itemFormData={aditionContractAmendment.items.formData}
                      onItemChange={aditionContractAmendment.items.handleChange}
                      itemFormErrors={form.errors}
                      onFormSubmit={
                        aditionContractAmendment.items
                          .handleSaveAditionContractAmendmentItems
                      }
                      initializeItemForm={
                        aditionContractAmendment.items
                          .initializeAditionContractAmendmentItemsForm
                      }
                      getOptions={aditionContractAmendment.items.getOptions}
                      getSelectedValue={
                        aditionContractAmendment.items.getSelectedValue
                      }
                      getSelectMeta={
                        aditionContractAmendment.items.getSelectMeta
                      }
                    />
                  </Box>

                  {/* Lista de JOBS */}
                  <Box sx={{ mt: "1rem" }}>
                    <ItemListSection
                      section={aditionContractAmendment.jobs.section}
                      description={aditionContractAmendment.jobs.description}
                      fields={aditionContractAmendment.jobs.fields}
                      initialData={
                        aditionContractAmendment.jobs
                          .aditionContractAmendmentJobs
                      }
                      onItemsChange={
                        aditionContractAmendment.jobs
                          .setAditionContractAmendmentJobs
                      }
                      renderFormBody={
                        aditionContractAmendment.jobs
                          .renderAditionContractAmendmentJobsFormBody
                      }
                      getItemDisplayValue={
                        aditionContractAmendment.jobs
                          .getAditionContractAmendmentJobsDisplayValue
                      }
                      itemFormData={aditionContractAmendment.jobs.formData}
                      onItemChange={aditionContractAmendment.jobs.handleChange}
                      itemFormErrors={form.errors}
                      onFormSubmit={
                        aditionContractAmendment.jobs
                          .handleSaveAditionContractAmendmentJobs
                      }
                      initializeItemForm={
                        aditionContractAmendment.jobs
                          .initializeAditionContractAmendmentJobsForm
                      }
                      getOptions={aditionContractAmendment.jobs.getOptions}
                      getSelectedValue={
                        aditionContractAmendment.jobs.getSelectedValue
                      }
                      getSelectMeta={
                        aditionContractAmendment.jobs.getSelectMeta
                      }
                    />
                  </Box>
                </div>
              )}
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditEventContractPage;
